import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Send, Check, ArrowRight, Phone, Mail } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import ProcessSection from '../components/ProcessSection';
import ContactSection from '../components/ContactSection';
import InstagramFeedSection from '../components/InstagramFeedSection';
import { FALLBACK_SERVICES, FALLBACK_PROJECTS, SITE_INFO } from '../data';
import { fetchCollection } from '../lib/cms';

export default function Home() {
  const [services, setServices] = useState(FALLBACK_SERVICES);
  const [projects, setProjects] = useState(FALLBACK_PROJECTS);

  useEffect(() => {
    async function loadData() {
      const cmsServices = await fetchCollection('services');
      if (cmsServices && cmsServices.length > 0) {
        setServices(cmsServices);
      }
      const cmsProjects = await fetchCollection('projects');
      if (cmsProjects && cmsProjects.length > 0) {
        setProjects(cmsProjects);
      }
    }
    loadData();
  }, []);

  return (
    <>
      <SEOHead 
        title={`${SITE_INFO.name} - Kvalitetstømrer & Snedkermester`}
        description="G28 ApS udfører kvalitetsbyggeri, tagrenovering, specialløsninger og ombygning for private boligejere og erhverv."
      />

      {/* Hero Section - Centered layout without webform */}
      <section className="relative bg-stone-900 text-white flex items-center justify-center overflow-hidden border-b border-stone-800 min-h-[85vh] sm:min-h-[75vh] lg:min-h-[85vh]">
        {/* Hero Background Image */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1920,quality=85,format=auto/assets/58b18d64-1cfa-413a-b5ba-90838c1f6d2c/skilt-03-photoshop-rez.jpg"
            alt="Arkitekttegnet villa med kvalitetstræbeklædning udført af G28 ApS"
            className="w-full h-full object-cover object-center animate-hero-zoom"
          />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-8 py-28 sm:py-28 lg:py-36 w-full text-center flex flex-col items-center">
          {/* Main Headline */}
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight leading-[1.12] max-w-4xl mb-6 drop-shadow-sm uppercase">
            Gennemført håndværk med sans for detaljen
          </h1>

          {/* Subline */}
          <p className="text-stone-100 text-base sm:text-lg lg:text-xl leading-relaxed font-normal max-w-2xl mb-10 drop-shadow-sm">
            Vi bygger, renoverer og skaber specialløsninger i kvalitets materialer for private og erhverv.
          </p>

          {/* CTAs */}
          <div className="flex flex-row flex-wrap sm:flex-nowrap items-center justify-center gap-4 text-sm sm:text-base w-full max-w-2xl">
            <Link
              to="/kontakt"
              className="inline-flex items-center justify-center gap-3 bg-amber-600 hover:bg-amber-500 text-white font-heading font-bold px-7 sm:px-8 py-4 shadow-xl transition-all hover:scale-[1.02] cursor-pointer whitespace-nowrap"
            >
              <Send className="w-5 h-5 text-white" aria-hidden="true" />
              <span className="whitespace-nowrap">Indhent et uforpligtende tilbud</span>
            </Link>
          </div>
        </div>
      </section>

      {/* Intro Section - Arpe Kjeldsholm inspired image + concise story */}
      <section className="py-20 bg-stone-50 text-stone-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            {/* Left Image: Authentic master craftsman photo */}
            <div className="lg:col-span-6 order-2 lg:order-1 hidden sm:block">
              <div className="relative">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/58b18d64-1cfa-413a-b5ba-90838c1f6d2c/image00001_33a66958.jpeg"
                  alt="Tømrer fra G28 i arbejde"
                  className="w-full h-[450px] sm:h-[520px] object-cover shadow-xl"
                />
              </div>
            </div>

            {/* Right Text Block */}
            <div className="lg:col-span-6 space-y-6 order-1 lg:order-2">
              <div>
                <h2 className="font-heading font-extrabold text-3xl sm:text-4xl text-stone-900 tracking-tight leading-tight uppercase">
                  FORANKRET I DANSK HÅNDVÆRK, DREVET AF FAGLIG STOLTHED
                </h2>
              </div>

              <p className="text-stone-700 text-base leading-relaxed">
                Hos G28 ApS får du solidt tømrerarbejde, god byggeledelse og en klar plan fra start til slut. Med tilsammen 35 års erfaring har vi den faglige viden og praktiske erfaring til at sikre et godt resultat.
              </p>

              <p className="text-stone-700 text-base leading-relaxed">
                Vi lægger vægt på ordentlig kommunikation, høj kvalitet og styr på både økonomi og tidsplan.
              </p>

              <div className="pt-2 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm font-semibold text-stone-800">
                <div className="flex items-center gap-2.5">
                  <Check className="w-5 h-5 text-stone-900 font-bold shrink-0" aria-hidden="true" />
                  <span>Fast og gennemsigtigt tilbud</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <Check className="w-5 h-5 text-stone-900 font-bold shrink-0" aria-hidden="true" />
                  <span>Dygtige og erfarne håndværkere</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <Check className="w-5 h-5 text-stone-900 font-bold shrink-0" aria-hidden="true" />
                  <span>God bygge- og projektstyring</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <Check className="w-5 h-5 text-stone-900 font-bold shrink-0" aria-hidden="true" />
                  <span>Realistisk tidsplan</span>
                </div>
                <div className="flex items-center gap-2.5 sm:col-span-2">
                  <Check className="w-5 h-5 text-stone-900 font-bold shrink-0" aria-hidden="true" />
                  <span>Én fast kontaktperson</span>
                </div>
              </div>

              <div className="pt-4 flex flex-wrap items-center gap-6">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center justify-center gap-2.5 bg-amber-600 hover:bg-amber-500 text-white font-heading font-bold px-6 py-3.5 shadow-md transition-all cursor-pointer whitespace-nowrap"
                >
                  <span>Kontakt os om dit projekt</span>
                  <ArrowRight className="w-4 h-4" aria-hidden="true" />
                </Link>
                <Link
                  to="/om-os"
                  className="inline-flex items-center gap-2 text-stone-900 hover:text-amber-700 font-heading font-bold text-sm border-b-2 border-stone-900 hover:border-amber-700 pb-1 transition-colors"
                >
                  <span>Læs mere om vores værdier</span>
                  <ArrowRight className="w-4 h-4" aria-hidden="true" />
                </Link>
              </div>

            </div>

          </div>
        </div>
      </section>

      {/* Services Showcase Section - Full-bleed multi-panel visual design */}
      <section className="relative bg-stone-900 text-white overflow-hidden border-b border-stone-800">
        {/* Floating action buttons container on right edge */}
        <div className="hidden lg:flex fixed right-6 top-1/2 -translate-y-1/2 z-30 flex-col gap-3">
          <a
            href={`tel:${SITE_INFO.phone.replace(/\s+/g, '')}`}
            title={`Ring direkte til ${SITE_INFO.name}`}
            className="w-12 h-12 bg-amber-600 hover:bg-amber-500 text-white flex items-center justify-center shadow-2xl transition-all hover:scale-110 cursor-pointer"
          >
            <Phone className="w-5 h-5" aria-hidden="true" />
          </a>
          <Link
            to="/kontakt"
            title="Indhent tilbud"
            className="w-12 h-12 bg-stone-900 border border-stone-700 hover:border-stone-500 text-white flex items-center justify-center shadow-2xl transition-all hover:scale-110 cursor-pointer"
          >
            <Mail className="w-5 h-5" aria-hidden="true" />
          </Link>
        </div>

        {/* Full-bleed vertical panels layout */}
        <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0 divide-y sm:divide-y-0 sm:divide-x divide-stone-700/50">
          {services
            .filter((srv) => srv.url_slug !== 'fundament-og-murerarbejde' && srv.url_slug !== 'nybyg')
            .slice(0, 4)
            .map((srv, index) => {
            const rawImg = Array.isArray(srv.featured_image) ? srv.featured_image[0] : srv.featured_image;
            const imgUrl = rawImg ? `https://assets.auxodev.dk/cdn-cgi/image/width=900,quality=85,format=auto/${rawImg.replace('https://assets.auxodev.dk/', '')}` : '';

            return (
              <Link
                key={srv.url_slug || srv.id || index}
                to={`/ydelser/${srv.url_slug}`}
                className="group relative h-[420px] sm:h-[500px] lg:h-[620px] w-full overflow-hidden flex flex-col justify-between p-6 sm:p-8 transition-all duration-500 bg-stone-800"
              >
                {/* Background image */}
                <div className="absolute inset-0 z-0">
                  {imgUrl ? (
                    <img
                      src={imgUrl}
                      alt={srv.title}
                      className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                    />
                  ) : (
                    <div className="w-full h-full bg-stone-800"></div>
                  )}
                  {/* Darkened overlay layers for better visual balance */}
                  <div className="absolute inset-0 bg-stone-950/50 group-hover:bg-stone-950/35 transition-colors duration-500"></div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/35 to-black/10"></div>
                </div>

                {/* Content: Vertical heading, and Link */}
                <div className="relative z-10 h-full flex flex-col justify-between items-start">
                  <div className="my-auto py-4 overflow-hidden">
                    <h3 className="font-heading font-extrabold text-2xl sm:text-3xl text-white group-hover:text-white transition-colors tracking-wide [writing-mode:vertical-rl] rotate-180 max-h-[360px] sm:max-h-[420px] py-2 uppercase drop-shadow-md">
                      {srv.title}
                    </h3>
                  </div>

                  <div className="flex items-center gap-2 text-xs font-bold text-white uppercase tracking-wider group-hover:text-white transition-colors drop-shadow-md">
                    <span>Udforsk</span>
                    <ArrowRight className="w-4 h-4 text-white group-hover:translate-x-1.5 transition-all" aria-hidden="true" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Process Section */}
      <ProcessSection />

      {/* Contact Section */}
      <ContactSection />

      {/* Instagram Feed / Embed Section */}
      <InstagramFeedSection />
    </>
  );
}
